import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Home } from 'lucide-react';

/**
 * Breadcrumbs
 *
 * Visible breadcrumb nav + BreadcrumbList JSON-LD schema. Emits the
 * schema in a Helmet-managed <script> tag so Google renders rich
 * breadcrumbs in the SERP snippet (~15% CTR uplift on average).
 *
 * Item shape: { name, url }. Home is implicit and prepended.
 */
const Breadcrumbs = ({ items, className = '' }) => {
  if (!items || items.length === 0) return null;

  // Build BreadcrumbList JSON-LD. Include implicit Home as the first
  // list item so the schema matches the visible breadcrumb order.
  // 2026-02-28: EVERY ListItem must have a non-empty `name` or Google
  // Search Console flags the schema. Coerce the label defensively and
  // skip any item that resolves to empty rather than emit an invalid
  // JSON-LD entry.
  const schemaItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.buildpilot.com.au/' },
    ...items
      .map((it, idx) => {
        const rawName = it.name != null ? String(it.name).trim() : '';
        if (!rawName) return null;
        return {
          '@type': 'ListItem',
          position: idx + 2,
          name: rawName,
          // On the last visible item Google recommends omitting the URL,
          // otherwise resolve relative to the canonical hostname.
          ...(idx === items.length - 1
            ? {}
            : { item: it.url && it.url.startsWith('http') ? it.url : `https://www.buildpilot.com.au${it.url || ''}` }),
        };
      })
      .filter(Boolean),
  ];
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: schemaItems,
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <nav 
        aria-label="Breadcrumb" 
        className={`py-3 ${className}`}
      >
      <ol className="flex items-center flex-wrap gap-1 text-sm md:text-sm" style={{ fontSize: '14px' }}>
        {/* Home is always first */}
        <li className="flex items-center">
          <Link 
            to="/" 
            className="text-[#86868b] hover:text-[#1d8efe] transition-colors flex items-center justify-center sm:justify-start gap-1.5 px-2 -mx-2 py-2 -my-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:px-0 sm:mx-0 sm:py-0"
            aria-label="Home"
          >
            <Home className="w-4 h-4" />
            <span className="sr-only sm:not-sr-only">Home</span>
          </Link>
        </li>

        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          
          return (
            <li key={item.url || index} className="flex items-center">
              <ChevronRight className="w-4 h-4 text-gray-400 mx-1 flex-shrink-0" />
              {isLast ? (
                <span 
                  className="text-[#1d1d1f] font-medium break-words py-2 sm:py-0"
                  aria-current="page"
                >
                  {item.name}
                </span>
              ) : (
                <Link 
                  to={item.url}
                  className="text-[#86868b] hover:text-[#1d8efe] transition-colors break-words py-2 -my-2 min-h-[44px] sm:min-h-0 sm:py-0 flex items-center"
                >
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
    </>
  );
};

// Wrapper with container for pages - accounts for fixed header.
// Accepts `items` (preferred) or a `breadcrumbs` alias, and tolerates the
// legacy { name, href } item shape by coercing href -> url, so a wrong prop
// name can never silently render an empty padded box again.
export const BreadcrumbsContainer = ({ items, breadcrumbs, variant = 'light' }) => {
  const bgClass = variant === 'dark' ? 'bg-[#1d1d1f]' : 'bg-white';
  const borderClass = variant === 'dark' ? 'border-gray-700' : 'border-gray-100';
  const raw = items || breadcrumbs || [];
  const resolved = raw.map((it) => (it && it.url == null && it.href != null ? { ...it, url: it.href } : it));

  return (
    <div className={`${bgClass} border-b ${borderClass} pt-28 sm:pt-[calc(var(--bp-hero-top))]`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={resolved} />
      </div>
    </div>
  );
};

export default Breadcrumbs;
